import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui/button";
import { Label } from "@patient-watch/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import { toast } from "@patient-watch/ui/sonner";
import { Textarea } from "@patient-watch/ui/textarea";
import { ExternalLink, Mail, Plus, Sparkles } from "lucide-react";
import { LeadGenDetailShell } from "@/features/lead-gen/components/LeadGenDetailShell";
import { runGenerateCopy } from "@/features/lead-gen/ai/runGenerateCopy";
import { getDefaultOutreachTemplateId, getOutreachTemplate } from "@/features/lead-gen/templates";
import { AiModelSelect } from "@/lib/ai/AiModelSelect";
import { usePreferredAiModel } from "@/lib/ai/usePreferredAiModel";
import { LeadIdentityAccordion } from "@/features/lead-gen/components/LeadIdentityAccordion";
import { TouchEditorCard } from "@/features/lead-gen/components/TouchEditorCard";
import { TouchKindIcon } from "@/features/lead-gen/components/TouchKindIcon";
import { useLeadGen } from "@/features/lead-gen/LeadGenContext";
import { formatLeadDate } from "@/features/lead-gen/schedule";
import {
    addTouchToLead,
    applyTouchChange,
    findCompanyForLead,
    isTouchReplyKind,
    leadColleagues,
    touchKindLabel,
    type Lead,
    type Touch,
} from "@/features/lead-gen/types";
import { useNav } from "@/utils/nav";

export const Route = createFileRoute("/_app/sales/leads/$leadId/")({
    component: LeadDetailPage,
    validateSearch: (search: Record<string, unknown>): { generate?: boolean } => {
        const generate = search.generate === true || search.generate === "1" || search.generate === "true";
        return generate ? { generate: true } : {};
    },
});

const autoGeneratedLeadIds = new Set<string>();

function LeadDetailPage() {
    const { leadId } = Route.useParams();
    const { generate } = Route.useSearch();
    const navigate = useNavigate();
    const { leads, setLeads, templates, artifacts, companies, enrichingLeadIds, enqueueLeadEmailEnrichment } =
        useLeadGen();
    const preferredModel = usePreferredAiModel();
    const [isGenerating, setIsGenerating] = useState(false);
    const [templateId, setTemplateId] = useState(() => getDefaultOutreachTemplateId(templates));
    const [prompt, setPrompt] = useState("");

    const lead = leads.find((row) => row.lead_id === leadId);

    useNav([
        { label: "Sales", route: { to: "/sales/leads" } },
        { label: "Leads", route: { to: "/sales/leads" } },
        {
            label: lead?.name || "Lead",
            route: { to: "/sales/leads/$leadId", params: { leadId } },
        },
    ]);

    const generateSequence = async () => {
        if (!lead) return;
        const template = getOutreachTemplate(templates, templateId);
        if (!template) {
            toast.error("Choose a template");
            return;
        }
        const hasWritten = lead.touches.some((touch) => touch.kind !== "connect" && touch.message.trim());
        setIsGenerating(true);
        try {
            const result = await runGenerateCopy({
                selectedLeads: [lead],
                allLeads: leads,
                template,
                artifacts,
                companies,
                prompt,
                model: preferredModel.choice
                    ? {
                          provider: preferredModel.choice.provider,
                          model: preferredModel.choice.model,
                      }
                    : null,
            });
            setLeads(result.leads);
            toast.success(
                `${hasWritten ? "Touches updated" : "Sequence generated"} for ${lead.name.trim() || "this lead"}`
            );
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Could not generate touches");
        } finally {
            setIsGenerating(false);
        }
    };

    useEffect(() => {
        if (!generate || !lead || autoGeneratedLeadIds.has(lead.lead_id)) return;
        autoGeneratedLeadIds.add(lead.lead_id);
        void navigate({
            to: "/sales/leads/$leadId",
            params: { leadId },
            search: {},
            replace: true,
        });
        void generateSequence();
        // Start once when arriving from add-lead. Later edits must not regenerate.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generate, lead, leadId, navigate]);

    if (!lead) {
        return (
            <LeadGenDetailShell
                backTo="/sales/leads"
                backLabel="All leads"
                summary={<p className="text-slate-500 text-sm">This lead may have been removed.</p>}
            >
                <p className="bg-white/70 p-6 border border-slate-200 rounded-lg text-slate-500 text-sm text-center">
                    Lead not found.
                </p>
            </LeadGenDetailShell>
        );
    }

    const company = findCompanyForLead(companies, lead);
    const colleagues = leadColleagues(leads, lead);
    const hasWrittenTouches = lead.touches.some(
        (touch) => touch.kind !== "connect" && !isTouchReplyKind(touch.kind) && touch.message.trim()
    );

    const updateLead = (updates: Partial<Lead>) => {
        setLeads((current) => current.map((row) => (row.lead_id === lead.lead_id ? { ...row, ...updates } : row)));
    };

    const updateTouch = (next: Touch) => {
        setLeads((current) => current.map((row) => (row.lead_id === lead.lead_id ? applyTouchChange(row, next) : row)));
    };

    const deleteTouch = (touchId: string) => {
        updateLead({ touches: lead.touches.filter((touch) => touch.touch_id !== touchId) });
    };

    return (
        <LeadGenDetailShell
            backTo="/sales/leads"
            backLabel="All leads"
            summary={
                <LeadIdentityAccordion
                    lead={lead}
                    company={company}
                    emailPending={enrichingLeadIds.includes(lead.lead_id)}
                    onChange={updateLead}
                />
            }
            actions={
                <div className="flex items-center gap-1.5">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="min-h-0 text-slate-500"
                        disabled={enrichingLeadIds.includes(lead.lead_id)}
                        onClick={() => enqueueLeadEmailEnrichment(lead, { force: true })}
                    >
                        <Mail className="w-3.5 h-3.5" />
                        {enrichingLeadIds.includes(lead.lead_id)
                            ? "Finding email..."
                            : lead.email_status === "not_found" || lead.email_status === "error"
                              ? "Try email again"
                              : lead.email.trim()
                                ? "Refresh email"
                                : "Find email"}
                    </Button>
                    {lead.linkedin_url.trim() ? (
                        <Button type="button" variant="ghost" size="sm" className="min-h-0 text-slate-500" asChild>
                            <a href={lead.linkedin_url} target="_blank" rel="noreferrer">
                                <ExternalLink className="w-3.5 h-3.5" />
                                LinkedIn
                            </a>
                        </Button>
                    ) : null}
                    <Button type="button" variant="ghost" size="sm" className="min-h-0 text-slate-500" asChild>
                        <Link to="/sales/leads/touches" search={{ lead: lead.lead_id }}>
                            Touches
                        </Link>
                    </Button>
                </div>
            }
        >
            <section className="space-y-3 bg-indigo-50/50 p-4 border border-indigo-200 rounded-lg">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-indigo-700" />
                    <div>
                        <h2 className="font-medium text-indigo-950 text-lg">Gen Touches</h2>
                        <p className="text-2xs text-slate-600">
                            Choose a template and tell AI what to create or change. The editable touches stay below.
                        </p>
                    </div>
                </div>

                <div className="gap-3 grid md:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="lead-touches-template">Template</Label>
                        <Select value={templateId} onValueChange={setTemplateId} disabled={isGenerating}>
                            <SelectTrigger id="lead-touches-template" className="bg-white h-12">
                                <SelectValue placeholder="Choose a template" />
                            </SelectTrigger>
                            <SelectContent>
                                {templates.map((template) => (
                                    <SelectItem key={template.id} value={template.id}>
                                        {template.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <AiModelSelect
                        id="lead-touches-model"
                        value={preferredModel.modelId}
                        options={preferredModel.options}
                        onValueChange={preferredModel.setModelId}
                        disabled={isGenerating}
                    />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="lead-touches-prompt">What should the touches say or change?</Label>
                    <Textarea
                        id="lead-touches-prompt"
                        value={prompt}
                        onChange={(event) => setPrompt(event.target.value)}
                        rows={4}
                        placeholder="e.g. Make the sequence shorter, mention their MSK work, and ask for 15 minutes next week"
                        className="bg-white text-sm resize-y"
                    />
                </div>

                <div className="flex justify-end">
                    <Button
                        type="button"
                        size="sm"
                        className="min-h-0"
                        disabled={isGenerating || !templateId}
                        onClick={() => void generateSequence()}
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        {isGenerating ? "Generating..." : hasWrittenTouches ? "Regenerate touches" : "Generate touches"}
                    </Button>
                </div>
            </section>

            <section className="space-y-3">
                <div className="flex flex-wrap justify-between items-center gap-2">
                    <div>
                        <h2 className="font-medium text-slate-800 text-lg">Touches</h2>
                        <p className="text-2xs text-slate-500">
                            Edit any message directly, or rewrite one with AI using this lead and the rest of the
                            sequence as context.
                            {company?.teams.length
                                ? ` Specialties at ${company.name}: ${company.teams.join(", ")}.`
                                : ""}
                        </p>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="min-h-0"
                        onClick={() => {
                            setLeads((current) => addTouchToLead(current, lead.lead_id));
                            toast.success("Touch added");
                        }}
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Add touch
                    </Button>
                </div>

                {lead.touches.length ? (
                    <div className="flex flex-col gap-3">
                        {lead.touches.map((touch) => (
                            <div key={touch.touch_id} className="space-y-1.5">
                                <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                                    <TouchKindIcon kind={touch.kind} />
                                    {touchKindLabel(touch.kind)}
                                    <span className="text-2xs text-slate-400">{formatLeadDate(touch.date)}</span>
                                    <Link
                                        to="/sales/leads/touches/$touchId"
                                        params={{ touchId: touch.touch_id }}
                                        className="ml-auto text-2xs text-slate-400 hover:text-slate-700 hover:underline underline-offset-2"
                                    >
                                        Open
                                    </Link>
                                </div>
                                <TouchEditorCard
                                    touch={touch}
                                    aiContext={{
                                        person: {
                                            name: lead.name,
                                            role: lead.role,
                                            email: lead.email,
                                            linkedin_url: lead.linkedin_url,
                                        },
                                        company: lead.company,
                                        companyContexts: company?.contexts,
                                        team: lead.team,
                                        otherTouches: lead.touches,
                                        colleagues,
                                        templates,
                                        artifacts,
                                    }}
                                    onChange={updateTouch}
                                    onDelete={() => deleteTouch(touch.touch_id)}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="bg-white/70 p-6 border border-slate-200 rounded-lg text-slate-500 text-sm text-center">
                        No touches yet.
                    </p>
                )}
            </section>
        </LeadGenDetailShell>
    );
}
